import { ChevronUp, ChevronDown, Plus } from 'lucide-react'
import { BlockPreview } from './BlockPreview'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import { COL_COLORS, COL_KEYS } from './canvasTypes'
import type { CanvasSlot, ColKey } from './canvasTypes'
import type { PageBlockType } from '../../api/types'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'

interface EditorialGridColumnsProps {
  slot: CanvasSlot
  assigningColKey: ColKey | null
  selectedColKey: ColKey | null
  selectedColBlockId: string | null
  onColumnClick: (colKey: ColKey, colLabel: string) => void
  onColBlockSelect: (colKey: ColKey, blockId: string, blockType: PageBlockType) => void
  onRemoveColBlock: (colKey: ColKey, blockId: string) => void
  onMoveColBlock: (colKey: ColKey, blockId: string, direction: 'up' | 'down') => void
}

export function EditorialGridColumns({
  slot,
  assigningColKey,
  selectedColKey,
  selectedColBlockId,
  onColumnClick,
  onColBlockSelect,
  onRemoveColBlock,
  onMoveColBlock,
}: EditorialGridColumnsProps) {
  const preset = (slot.config.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
  const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
  const colKeys = COL_KEYS.slice(0, presetCfg.columns)
  const colLabels = ['Column 1', 'Column 2', 'Column 3'].slice(0, presetCfg.columns)

  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: presetCfg.gridTemplate }}
      className="divide-x divide-line bg-canvas"
    >
      {colKeys.map((colKey, i) => {
        const colBlocks = slot.columnBlocks?.[colKey] ?? []
        const isActive = assigningColKey === colKey
        const color = COL_COLORS[i]
        return (
          <div key={colKey} className="flex min-w-0 flex-col overflow-hidden">
            {/* Column header */}
            <div
              className={`flex items-center gap-1.5 border-b px-2 py-1 ${isActive ? 'border-info-line bg-info-fill' : 'border-line bg-surface'}`}
            >
              <span className={`inline-block h-2 w-2 rounded-full ${color.dot}`} />
              <span className={`text-[9px] font-bold uppercase tracking-widest ${color.label}`}>
                {colLabels[i]}
              </span>
              <span className="ml-auto text-[9px] text-fg-secondary opacity-50">
                {colBlocks.length} block{colBlocks.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Block list */}
            {colBlocks.map((colBlock, bi) => {
              const colDef = PAGE_BLOCK_REGISTRY[colBlock.type] ?? { label: colBlock.type, defaultConfig: {} }
              const isThisSelected = selectedColKey === colKey && selectedColBlockId === colBlock.id
              return (
                <div
                  key={colBlock.id}
                  className={`cursor-pointer border-b border-line transition-colors ${isThisSelected ? 'ring-2 ring-inset ring-gray-800 dark:ring-gray-200' : 'hover:bg-fill'}`}
                  onClick={(e) => { e.stopPropagation(); onColBlockSelect(colKey, colBlock.id, colBlock.type) }}
                >
                  <div className="flex items-center justify-between border-b border-line/40 px-2 py-0.5">
                    <span className="text-[9px] font-semibold text-fg-secondary">{colDef.label}</span>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onMoveColBlock(colKey, colBlock.id, 'up') }}
                        disabled={bi === 0}
                        className="rounded px-0.5 py-0.5 text-fg-secondary hover:bg-fill disabled:opacity-30"
                        aria-label="Move up"
                      >
                        <ChevronUp size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onMoveColBlock(colKey, colBlock.id, 'down') }}
                        disabled={bi === colBlocks.length - 1}
                        className="rounded px-0.5 py-0.5 text-fg-secondary hover:bg-fill disabled:opacity-30"
                        aria-label="Move down"
                      >
                        <ChevronDown size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRemoveColBlock(colKey, colBlock.id) }}
                        className="ml-0.5 rounded px-1 text-[10px] text-fg-secondary hover:bg-red-9 hover:text-white"
                        aria-label="Remove block"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="pointer-events-none">
                    <BlockPreview type={colBlock.type} compact />
                  </div>
                </div>
              )
            })}

            {/* Add Block button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onColumnClick(colKey, colLabels[i]) }}
              className={`flex items-center justify-center gap-1 py-2 text-[11px] font-semibold transition-colors ${
                isActive ? 'bg-info-fill text-info-fg' : 'text-blue-11 hover:bg-blue-3'
              }`}
            >
              <Plus size={11} aria-hidden="true" />
              Add Block
            </button>
          </div>
        )
      })}
    </div>
  )
}
